import { h } from 'preact';
import { loadBaseMovies } from 'utils/baseMovies';
import { duplicateTitle, missingReleaseDate, duplicateSlug, validateSlug } from 'utils/errors';
import { isPowsterEmployee } from 'utils/roles';
import slugify from 'utils/slugify';
import { getCurrentRegionCode } from 'utils/studioConfig';

import Button from 'components/button/button';
import AutocompleteField from 'components/editor/settings/autocompleteField';
import AddMovieModal from 'components/movies/addMovieModal/addMovieModal';

import s from 'components/movies/addMovieModal/addMovieModalUniversal.sss';

export default class UniversalAddMovieModal extends AddMovieModal {
	s = s;

	get fields() {
		return {
			baseId: { render: this.renderBaseField, validators: [this.missingBase] },
			title: { render: this.renderTitleField, validators: [this.duplicateTitle] },
			release: { render: this.renderReleaseDateField, validators: [missingReleaseDate] },
			slug: { render: this.renderSlugField, validators: [duplicateSlug, validateSlug] },
		};
	}

	get canCreateBaseMovie() {
		// TODO: Implement as a permission check, maybe? Otherwise not very reusable...
		const regionCode = getCurrentRegionCode();
		return isPowsterEmployee() || regionCode === 'us';
	}

	constructor(props) {
		super(props);

		this.setState({
			availableMovies: {},
			newMovieRequested: false,
		});

		this.disableCreateNewMovie = this.disableCreateNewMovie.bind(this);
		this.duplicateTitle = this.duplicateTitle.bind(this);
		this.missingBase = this.missingBase.bind(this);
		this.onBaseIdChange = this.onBaseIdChange.bind(this);
		this.openRequestMovieEmail = this.openRequestMovieEmail.bind(this);
		this.renderBaseField = this.renderBaseField.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();
		this.loadAvailableMovies();
	}

	onBaseIdChange(e) {
		const { custom, name, value } = e.target || {};
		this.setValue(name, value);
		this.setState({ newMovieRequested: custom });
	}

	getNewMovieOptions() {
		const { data, newMovieRequested } = this.state;
		const { baseId, title, release, slug } = data || {};
		const baseMovieTitle = this.getSelectedBaseMovieTitle();

		return {
			title,
			release,
			slug: slug || slugify(title || baseMovieTitle || '', { removeDiacritics: true }),
			// If a new movie is requested, we send the base title as part of the request to generate a new base movie entry
			...(newMovieRequested ? { base: { title: baseMovieTitle } } : {}),
			// If a new movie is not requested (linking to existing), we send the base movie id
			...(!newMovieRequested ? { baseId } : {}),
		};
	}

	getSelectedBaseMovieTitle() {
		const { availableMovies } = this.state;
		const baseId = this.getValue('baseId');
		return availableMovies?.[baseId] || baseId || '';
	}

	async loadAvailableMovies() {
		try {
			const { enabled, notEnabled } = await loadBaseMovies(true);

			const availableMovies = Object.fromEntries(
				enabled
					.filter(x => !x.localised_movie)
					.map(x => ([x.base_movie.id, x.base_movie.title]))
			);
			const baseMovies = Object.fromEntries(
				[...enabled.map(x => x.base_movie), ...notEnabled].map(x => ([x.id, x.title]))
			);

			this.setState({ availableMovies, baseMovies });
		} catch (error) {
			console.error('Failed to load base movies', error);
		}
	}

	openRequestMovieEmail() {
		const { data } = this.state;
		const baseId = this.getValue('baseId');
		const { release, slug, title } = data || {};
		const regionCode = getCurrentRegionCode().toUpperCase();

		const movieTitle = baseId; // baseId is the movie title when custom
		const subject = `Universal Pictures ${regionCode}: New Movie Request - ${movieTitle} [Powster CMS]`;
		const details = [
			title && `Localized Title: ${title}`,
			release && `Release Date: ${release}`,
			slug && `Movie Slug: ${slug}`,
		].filter(Boolean).join('\n');
		const body = `Hello Universal CMS Team,\n\nI would like to request a new movie entry for "${movieTitle}".${details ? `\n\n${details}` : ''}\n\nThank you.`;
		const mailtoLink = `mailto:universalcmsrequest@powster.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
		
		window.open(mailtoLink, '_blank');
	}

	duplicateTitle() {
		const { availableMovies, data } = this.state;
		const { title, baseId } = data || {};

		if (!title && baseId) {
			return duplicateTitle(availableMovies?.[baseId]);
		}

		return duplicateTitle(title);
	}

	missingBase() {
		const baseId = this.getValue('baseId');
		if (!baseId) {
			return 'Please select a title for the movie.';
		}
		return null;
	}

	getBaseFieldPreText() {
		const baseId = this.getValue('baseId');
		if (!baseId) return '';

		if (this.state.newMovieRequested) {
			return 'New movie';
		}

		return 'Linked movie';
	}

	disableCreateNewMovie(search) {
		if (Array.isArray(search)) search = search.join('');

		if (!this.canCreateBaseMovie) {
			return false;
		}

		const baseTitles = Object.values(this.state.baseMovies || {});
		if (!search || baseTitles.some(title => title.toLowerCase() === search.toLowerCase())) {
			return true;
		}

		return false;
	}

	renderBaseField() {
		const { availableMovies } = this.state;
		const preText = this.getBaseFieldPreText();
		const noOptions = !Object.keys(availableMovies).length;

		return (
			<AutocompleteField
				key="baseId"
				name="baseId"
				label="Movie title"
				help="Displays titles using the OV (Original Version)"
				placeholder="Search or enter new title..."
				value={this.getValue('baseId')}
				preText={preText}
				onChange={this.onBaseIdChange}
				error={this.getError('baseId')}
				allowClear
			>
				{noOptions && <option value="NO_OPTIONS" disabled>No options available</option>}
				{Object.entries(availableMovies).map(([id, title]) => (
					<option key={id} value={id}>{title}</option>
				))}
				<hr />
				<option data-custom icon="plus" shouldDisable={this.disableCreateNewMovie}>
					{this.canCreateBaseMovie ? 'Create' : 'Request'} new entry for "%s"
				</option>
			</AutocompleteField>
		);
	}

	renderTitleField() {
		const regionCode = getCurrentRegionCode().toUpperCase();
		const placeholder = this.getSelectedBaseMovieTitle();
		const disabled = !this.getValue('baseId');

		return super.renderTitleField({ 
			label: `Localized title (${regionCode})`,
			note: 'Only for the currently selected locale',
			placeholder,
			disabled,
		});
	}

	renderReleaseDateField() {
		const disabled = !this.getValue('baseId');

		return super.renderReleaseDateField({
			disabled
		});
	}

	renderSlugField() {
		const movieTitle = this.getValue('title') || this.getSelectedBaseMovieTitle();
		const disabled = !this.getValue('baseId');

		return super.renderSlugField({
			placeholder: slugify(movieTitle, { removeDiacritics: true }),
			disabled,
		});
	}

	renderContent() {
		const base = super.renderContent();

		// Add notice for requesting a new movie
		if (base && this.state.newMovieRequested && !this.canCreateBaseMovie) {
			base.children.push(
				<p>Fill out the above information and contact Powster to request a new movie.</p>
			);
		}

		return base;
	}

	renderContactPowsterButton() {
		return (
			<Button
				type="button"
				primary
				iconAlt="share"
				onClick={this.openRequestMovieEmail}
			>
				Contact Powster
			</Button>
		);
	}

	renderActionButton() {
		if (this.state.newMovieRequested && !this.canCreateBaseMovie) {
			return this.renderContactPowsterButton();
		}

		return super.renderActionButton();
	}
}
